import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import ReactMarkdown from "react-markdown";
import { getAdr } from "@/content/adr";

export const Route = createFileRoute("/admin/references/$id")({
  loader: ({ params }) => {
    const adr = getAdr(params.id);
    if (!adr) throw notFound();
    return { adr };
  },
  component: AdrView,
  notFoundComponent: () => (
    <div className="space-y-3">
      <p className="text-ink-secondary">Reference not found.</p>
      <Link to="/admin/references" className="text-primary underline underline-offset-4 text-sm">
        ← Back to references
      </Link>
    </div>
  ),
});

function AdrView() {
  const { adr } = Route.useLoaderData();
  return (
    <article className="space-y-6">
      <div>
        <Link
          to="/admin/references"
          className="text-xs font-mono text-ink-tertiary hover:text-primary"
        >
          ← All references
        </Link>
      </div>
      <header className="border-b border-border pb-4">
        <p className="font-mono text-xs text-ink-tertiary">
          {adr.number} · {adr.status} · {adr.date}
        </p>
        <h2 className="font-serif text-3xl text-primary mt-2">{adr.title}</h2>
      </header>
      <div className="space-y-4 text-ink-primary leading-relaxed">
        <ReactMarkdown
          components={{
            h1: () => null,
            h2: (p) => <h3 className="font-serif text-xl text-primary mt-8 mb-2">{p.children}</h3>,
            h3: (p) => <h4 className="overline mt-6 mb-2">{p.children}</h4>,
            p: (p) => <p className="font-serif text-base">{p.children}</p>,
            ul: (p) => <ul className="list-disc list-outside ml-6 space-y-1 font-serif">{p.children}</ul>,
            ol: (p) => <ol className="list-decimal list-outside ml-6 space-y-1 font-serif">{p.children}</ol>,
            li: (p) => <li className="font-serif">{p.children}</li>,
            strong: (p) => <strong className="text-primary font-semibold">{p.children}</strong>,
            a: (p) => (
              <a
                href={p.href}
                target="_blank"
                rel="noreferrer"
                className="text-primary underline underline-offset-4 hover:no-underline"
              >
                {p.children}
              </a>
            ),
            code: (p) => (
              <code className="font-mono text-xs bg-muted px-1 py-0.5">{p.children}</code>
            ),
            table: (p) => (
              <div className="overflow-x-auto my-4">
                <table className="w-full text-sm border-collapse">{p.children}</table>
              </div>
            ),
            th: (p) => (
              <th className="text-left font-mono text-xs uppercase tracking-wider border-b border-border px-2 py-2 text-ink-secondary">
                {p.children}
              </th>
            ),
            td: (p) => <td className="border-b border-border/50 px-2 py-2 align-top font-serif">{p.children}</td>,
            hr: () => <hr className="my-6 border-border" />,
          }}
        >
          {adr.body}
        </ReactMarkdown>
      </div>
    </article>
  );
}
